/**
 * ============================================================================
 * DATA TABLE — generic, config-driven read-only table.
 * ============================================================================
 *
 * The single implementation for "reference" tables (glossary, definitions,
 * static lists). Declares columns as data, not JSX — adding a table anywhere
 * in the app is a column config + a data array, never hand-written <tr> loops.
 *
 * WHEN TO USE WHICH TABLE (project convention):
 * - DataTable        → read-only reference/list tables (this file)
 * - AG Grid          → interactive pivots: column grouping, cell class rules,
 *                      row expansion (MetricTableSection, DCR drilldown)
 * - Cloudscape Table → only when native Cloudscape look is required AND no
 *                      custom header styling is needed
 *
 * FEATURES:
 * - `singleLine`      one line per row, ellipsis on overflow, hover tooltip
 * - `maxVisibleRows`  cap visible height at N rows, scroll + sticky header
 * - `resizable`       drag column borders to resize (default ON)
 * - Column header menu (click a header): sort asc/desc, hide column,
 *   move left/right, freeze up to this column, reset visual. All state is
 *   per-table component state (resets on reload).
 *
 * SORTING: columns are sorted by `sortValue(row)` if provided, else by the
 * render output when it is a string/number. Columns whose render returns
 * complex nodes and provide no sortValue fall back to string comparison.
 *
 * THEMING: header colour comes from the `headerTheme` prop mapped to design
 * tokens in DataTable.scss — never inline hex in consumers.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './DataTable.scss';

export interface DataTableColumn<T> {
  id: string;
  header: string;
  /** Initial width — percentage ('20%') or px ('150px'). Omit to auto-size. */
  width?: string;
  /** Cell renderer. Return a string or any ReactNode. */
  render: (row: T) => React.ReactNode;
  /** Value used for sorting; defaults to render output when primitive. */
  sortValue?: (row: T) => string | number;
}

export type DataTableHeaderTheme = 'blue' | 'navy';

/** Keep in sync with row padding/line-height in DataTable.scss. */
const ROW_HEIGHT_PX = 39;
const HEADER_HEIGHT_PX = 40;
const MIN_COL_WIDTH_PX = 60;

type SortDir = 'asc' | 'desc';

interface DataTableProps<T> {
  columns: Array<DataTableColumn<T>>;
  items: T[];
  /** Stable row key extractor — required for correct React reconciliation. */
  rowKey: (row: T) => string;
  headerTheme?: DataTableHeaderTheme;
  /** One line per row, ellipsis on overflow, full text on hover. */
  singleLine?: boolean;
  /** Show at most N rows; the rest scroll within the table (sticky header). */
  maxVisibleRows?: number;
  /** Drag column header edges to resize. Default true. */
  resizable?: boolean;
  /** Message shown when items is empty. */
  emptyText?: string;
}

export function DataTable<T>({
  columns,
  items,
  rowKey,
  headerTheme = 'blue',
  singleLine = false,
  maxVisibleRows,
  resizable = true,
  emptyText = 'No data available.',
}: DataTableProps<T>): React.ReactElement {
  // ---- view state (all reset by "Reset visual") ----
  const [pxWidths, setPxWidths] = useState<Map<string, number> | null>(null);
  const [sort, setSort] = useState<{ colId: string; dir: SortDir } | null>(null);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [order, setOrder] = useState<string[]>(() => columns.map((c) => c.id));
  const [frozenUpTo, setFrozenUpTo] = useState<string | null>(null);
  const [menuColId, setMenuColId] = useState<string | null>(null);

  const tableRef = useRef<HTMLTableElement>(null);
  const dragState = useRef<{ colId: string; startX: number; startWidth: number } | null>(null);

  /** Visible columns in display order. */
  const visibleColumns = useMemo(
    () =>
      order
        .map((id) => columns.find((c) => c.id === id))
        .filter((c): c is DataTableColumn<T> => !!c && !hidden.has(c.id)),
    [order, columns, hidden],
  );

  const frozenCount = useMemo(() => {
    if (!frozenUpTo) return 0;
    const idx = visibleColumns.findIndex((c) => c.id === frozenUpTo);
    return idx === -1 ? 0 : idx + 1;
  }, [frozenUpTo, visibleColumns]);

  // ---- sorting ----
  const sortedItems = useMemo(() => {
    if (!sort) return items;
    const col = columns.find((c) => c.id === sort.colId);
    if (!col) return items;
    const value = (row: T): string | number => {
      if (col.sortValue) return col.sortValue(row);
      const rendered = col.render(row);
      return typeof rendered === 'string' || typeof rendered === 'number'
        ? rendered
        : String(rendered);
    };
    const factor = sort.dir === 'asc' ? 1 : -1;
    return [...items].sort((a, b) => {
      const va = value(a);
      const vb = value(b);
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * factor;
      return String(va).localeCompare(String(vb)) * factor;
    });
  }, [items, sort, columns]);

  // ---- widths: snapshot rendered px widths (needed by resize + freeze) ----
  const snapshotWidths = useCallback((): Map<string, number> => {
    const snap = new Map<string, number>();
    const ths = tableRef.current?.querySelectorAll('thead th');
    ths?.forEach((th) => {
      const id = (th as HTMLElement).dataset.colId;
      if (id) snap.set(id, (th as HTMLElement).offsetWidth);
    });
    setPxWidths(snap);
    return snap;
  }, []);

  // ---- resize ----
  const onMouseMove = useCallback((e: MouseEvent) => {
    const drag = dragState.current;
    if (!drag) return;
    setPxWidths((prev) => {
      if (!prev) return prev;
      const next = new Map(prev);
      next.set(drag.colId, Math.max(MIN_COL_WIDTH_PX, drag.startWidth + e.clientX - drag.startX));
      return next;
    });
  }, []);

  const onMouseUp = useCallback(() => {
    dragState.current = null;
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, [onMouseMove]);

  const startResize = useCallback(
    (colId: string, e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const widths = pxWidths ?? snapshotWidths();
      dragState.current = { colId, startX: e.clientX, startWidth: widths.get(colId) ?? 150 };
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [pxWidths, snapshotWidths, onMouseMove, onMouseUp],
  );

  // ---- header menu ----
  useEffect(() => {
    if (!menuColId) return undefined;
    const close = () => setMenuColId(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [menuColId]);

  const moveColumn = (colId: string, dir: -1 | 1) => {
    setOrder((prev) => {
      const idx = prev.indexOf(colId);
      const target = idx + dir;
      if (idx === -1 || target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const resetVisual = () => {
    setPxWidths(null);
    setSort(null);
    setHidden(new Set());
    setOrder(columns.map((c) => c.id));
    setFrozenUpTo(null);
  };

  // ---- layout helpers ----
  const colStyle = (col: DataTableColumn<T>): React.CSSProperties | undefined => {
    if (pxWidths) return { width: pxWidths.get(col.id) };
    return col.width ? { width: col.width } : undefined;
  };

  /**
   * When column configs use px widths, size the table to their sum so it can
   * exceed the container and scroll horizontally WITHIN the table wrapper
   * (the page never scrolls sideways). Percentage widths keep width: 100%.
   */
  const configPxTotal = useMemo(() => {
    let total = 0;
    for (const c of visibleColumns) {
      if (!c.width || !c.width.endsWith('px')) return null;
      total += parseFloat(c.width);
    }
    return total;
  }, [visibleColumns]);

  /** Sticky left offset for frozen columns (requires px widths). */
  const frozenLeft = (index: number): number => {
    let left = 0;
    for (let i = 0; i < index; i++) {
      left += pxWidths?.get(visibleColumns[i].id) ?? 0;
    }
    return left;
  };

  const freezeUpTo = (colId: string) => {
    // Sticky offsets need concrete px widths — snapshot before freezing.
    if (!pxWidths) snapshotWidths();
    setFrozenUpTo(colId);
  };

  const cellText = (node: React.ReactNode): string | undefined =>
    typeof node === 'string' || typeof node === 'number' ? String(node) : undefined;

  const frozenStyle = (index: number): React.CSSProperties | undefined =>
    index < frozenCount && pxWidths ? { position: 'sticky', left: frozenLeft(index) } : undefined;

  const scrollStyle: React.CSSProperties | undefined = maxVisibleRows
    ? { maxHeight: HEADER_HEIGHT_PX + ROW_HEIGHT_PX * maxVisibleRows, overflowY: 'auto' }
    : undefined;

  const table = (
    <table
      ref={tableRef}
      className={`data-table data-table--${headerTheme}${singleLine ? ' data-table--single-line' : ''}`}
      style={
        pxWidths
          ? { width: visibleColumns.reduce((sum, c) => sum + (pxWidths.get(c.id) ?? 0), 0) }
          : configPxTotal
            ? { width: configPxTotal }
            : undefined
      }
    >
      <colgroup>
        {visibleColumns.map((c) => (
          <col key={c.id} style={colStyle(c)} />
        ))}
      </colgroup>
      <thead>
        <tr>
          {visibleColumns.map((c, i) => (
            <th
              key={c.id}
              data-col-id={c.id}
              className={i < frozenCount ? 'data-table__frozen' : undefined}
              style={frozenStyle(i)}
            >
              <span
                className="data-table__header-label"
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuColId(menuColId === c.id ? null : c.id);
                }}
              >
                {c.header}
                {sort?.colId === c.id && (
                  <span className="data-table__sort-indicator">{sort.dir === 'asc' ? ' ▲' : ' ▼'}</span>
                )}
              </span>

              {menuColId === c.id && (
                <div className="data-table__menu" onClick={(e) => e.stopPropagation()}>
                  <div className="data-table__menu-section">Sort order</div>
                  <button onClick={() => { setSort({ colId: c.id, dir: 'asc' }); setMenuColId(null); }}>
                    Ascending
                  </button>
                  <button onClick={() => { setSort({ colId: c.id, dir: 'desc' }); setMenuColId(null); }}>
                    Descending
                  </button>
                  <div className="data-table__menu-divider" />
                  <button
                    disabled={visibleColumns.length <= 1}
                    onClick={() => { setHidden((p) => new Set(p).add(c.id)); setMenuColId(null); }}
                  >
                    Hide
                  </button>
                  <button disabled={i === 0} onClick={() => { moveColumn(c.id, -1); setMenuColId(null); }}>
                    Move left
                  </button>
                  <button
                    disabled={i === visibleColumns.length - 1}
                    onClick={() => { moveColumn(c.id, 1); setMenuColId(null); }}
                  >
                    Move right
                  </button>
                  <div className="data-table__menu-divider" />
                  <button onClick={() => { freezeUpTo(c.id); setMenuColId(null); }}>
                    Freeze up to this column
                  </button>
                  {frozenUpTo && (
                    <button onClick={() => { setFrozenUpTo(null); setMenuColId(null); }}>
                      Unfreeze all
                    </button>
                  )}
                  <div className="data-table__menu-divider" />
                  <button onClick={() => { resetVisual(); setMenuColId(null); }}>Reset visual</button>
                </div>
              )}

              {resizable && (
                <span
                  className="data-table__resize-handle"
                  onMouseDown={(e) => startResize(c.id, e)}
                  role="separator"
                  aria-orientation="vertical"
                  aria-label={`Resize ${c.header} column`}
                />
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sortedItems.length === 0 ? (
          <tr>
            <td colSpan={visibleColumns.length} className="data-table__empty">
              {emptyText}
            </td>
          </tr>
        ) : (
          sortedItems.map((row) => (
            <tr key={rowKey(row)}>
              {visibleColumns.map((c, i) => {
                const content = c.render(row);
                return (
                  <td
                    key={c.id}
                    className={i < frozenCount ? 'data-table__frozen' : undefined}
                    style={frozenStyle(i)}
                    title={singleLine ? cellText(content) : undefined}
                  >
                    {content}
                  </td>
                );
              })}
            </tr>
          ))
        )}
      </tbody>
    </table>
  );

  return scrollStyle ? (
    <div className="data-table-scroll" style={{ ...scrollStyle, overflowX: 'auto' }}>
      {table}
    </div>
  ) : (
    <div className="data-table-hscroll">{table}</div>
  );
}
